import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('ticket_token'));
  const [loading, setLoading] = useState(true);
  const [pending2FA, setPending2FA] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const checkAuth = async () => {
      const savedToken = localStorage.getItem('ticket_token');
      if (savedToken) {
        try {
          const response = await fetch(`${API_URL}/api/auth/me`, {
            headers: {
              'Authorization': `Bearer ${savedToken}`
            }
          });
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            setToken(savedToken);
          } else {
            localStorage.removeItem('ticket_token');
            setToken(null);
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('ticket_token');
          setToken(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [API_URL]);

  const login = async (email, password) => {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      throw new Error('Bad username or password');
    }

    const data = await response.json();
    
    // Check if 2FA is required
    if (data.requires_2fa) {
      setPending2FA({ tempToken: data.temp_token, email });
      return { requires_2fa: true };
    }
    
    localStorage.setItem('ticket_token', data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const verify2FA = async (code) => {
    if (!pending2FA) {
      throw new Error('No 2FA verification pending');
    }
    
    try {
      const response = await fetch(`${API_URL}/api/auth/verify-2fa?temp_token=${pending2FA.tempToken}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Invalid 2FA code');
      }

      localStorage.setItem('ticket_token', data.access_token);
      setToken(data.access_token);
      setUser(data.user);
      setPending2FA(null);
      return data.user;
    } catch (error) {
      // Handle JSON parse errors or network errors
      if (error.message.includes('body stream already read') || error.message.includes('JSON')) {
        throw new Error('Invalid 2FA code');
      }
      throw error;
    }
  };

  const cancel2FA = () => {
    setPending2FA(null);
  };

  const register = async (userData) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }

      localStorage.setItem('ticket_token', data.access_token);
      setToken(data.access_token);
      setUser(data.user);
      return data.user;
    } catch (error) {
      if (error.message.includes('already registered')) {
        throw new Error('Email already registered');
      }
      throw new Error(error.message || 'Registration failed');
    }
  };

  const logout = () => {
    localStorage.removeItem('ticket_token');
    setToken(null);
    setUser(null);
    setPending2FA(null);
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        }
      } catch (error) {
        console.error('Failed to refresh user:', error);
      }
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      pending2FA,
      login, 
      verify2FA, 
      cancel2FA,
      register, 
      logout,
      refreshUser 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
